import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchAgentOpsWebsitesQuery } from "../../../app/api/console/agentOperationsApiSlice";

// Normalize a URL/domain to a dedupe key (host, no scheme/www/path).
function normKey(url) {
  let u = (url || "").trim().toLowerCase();
  if (!u) return "";
  if (!u.includes("//")) u = "https://" + u;
  try {
    const host = new URL(u).hostname.replace(/^www\./, "");
    return host;
  } catch {
    return u.replace(/^https?:\/\//, "").replace(/^www\./, "").split("/")[0];
  }
}
function looksLikeUrl(s) {
  const t = (s || "").trim();
  return t.includes(".") && !t.includes(" ");
}

export default function WebsitePicker({ selected, onChange }) {
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [open, setOpen] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [bulk, setBulk] = useState("");
  const boxRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  const { data, isFetching } = useSearchAgentOpsWebsitesQuery(debounced, {
    skip: debounced.length < 2,
  });

  const selectedKeys = useMemo(() => new Set(selected.map((s) => normKey(s.url))), [selected]);
  const results = (data?.results || []).filter((r) => !selectedKeys.has(normKey(r.website_url)));

  useEffect(() => {
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const addItem = (item) => {
    const key = normKey(item.url);
    if (!key || selectedKeys.has(key)) return;
    onChange([...selected, item]);
  };
  const addExisting = (r) => {
    addItem({ url: r.website_url, name: r.website_name, website_id: r.website_id, source: "existing" });
    setQuery("");
    setOpen(false);
  };
  const addManual = () => {
    const t = query.trim();
    if (!looksLikeUrl(t)) return;
    addItem({ url: t, source: "manual" });
    setQuery("");
    setOpen(false);
  };
  const addBulk = () => {
    const lines = bulk.split("\n").map((s) => s.trim()).filter((s) => s && !s.startsWith("#"));
    const next = [...selected];
    const seen = new Set(next.map((s) => normKey(s.url)));
    for (const line of lines) {
      const k = normKey(line);
      if (k && !seen.has(k)) {
        seen.add(k);
        next.push({ url: line, source: "manual" });
      }
    }
    onChange(next);
    setBulk("");
    setShowBulk(false);
  };
  const remove = (idx) => onChange(selected.filter((_, i) => i !== idx));

  const existingCount = selected.filter((s) => s.source === "existing").length;

  const label = "block text-body-xs font-semibold text-text-secondary mb-1";
  const input = "w-full px-3 py-2 text-body-xs bg-white border border-border-default rounded-xl focus:outline-none focus:ring-2 focus:ring-accent-purple/20 focus:border-accent-purple/40";

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className={label}>
          Websites <span className="text-text-tertiary font-normal">({selected.length}{existingCount ? ` · ${existingCount} existing` : ""})</span>
        </label>
        <button type="button" onClick={() => setShowBulk((v) => !v)} className="text-[11px] text-accent-purple hover:underline">
          {showBulk ? "hide bulk paste" : "bulk paste URLs"}
        </button>
      </div>

      {/* Search + autocomplete */}
      <div className="relative" ref={boxRef}>
        <input
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); if (results[0] && !looksLikeUrl(query)) addExisting(results[0]); else addManual(); }
          }}
          placeholder="Search existing websites by name or URL, or type a URL + Enter to add a new one…"
          className={input}
        />
        {open && (debounced.length >= 2) && (
          <div className="absolute z-20 mt-1 w-full bg-white border border-border-default rounded-xl shadow-lg max-h-64 overflow-y-auto">
            {isFetching && <div className="px-3 py-2 text-body-xs text-text-tertiary">Searching…</div>}
            {!isFetching && results.map((r) => (
              <button key={r.website_id} type="button" onClick={() => addExisting(r)}
                className="w-full text-left px-3 py-2 hover:bg-surface-neutral flex items-center justify-between gap-2">
                <span className="min-w-0">
                  <span className="text-body-xs font-medium text-text-primary">{r.website_name || r.website_url}</span>
                  <span className="block text-[11px] text-text-tertiary truncate">{r.website_url}</span>
                </span>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-700 shrink-0">existing</span>
              </button>
            ))}
            {!isFetching && !results.length && (
              <div className="px-3 py-2 text-body-xs text-text-tertiary">
                No existing match.{looksLikeUrl(query) ? " Press Enter to add it as a new website." : ""}
              </div>
            )}
          </div>
        )}
      </div>

      {showBulk && (
        <div className="mt-2">
          <textarea rows={4} value={bulk} onChange={(e) => setBulk(e.target.value)}
            placeholder={"Paste many URLs, one per line\nhttps://brand-a.com\nhttps://brand-b.com"}
            className={`${input} font-mono`} />
          <div className="flex justify-end mt-1">
            <button type="button" onClick={addBulk} className="px-3 py-1.5 text-[11px] font-semibold border border-border-default rounded-lg text-text-secondary hover:bg-surface-neutral">
              Add all
            </button>
          </div>
        </div>
      )}

      {/* Selected chips */}
      {selected.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5 max-h-40 overflow-y-auto p-2 border border-border-subtle rounded-xl bg-surface-neutral/30">
          {selected.map((s, i) => (
            <span key={`${s.url}-${i}`} className="inline-flex items-center gap-1.5 pl-2 pr-1 py-1 bg-white border border-border-default rounded-lg text-[11px]">
              <span className={`w-1.5 h-1.5 rounded-full ${s.source === "existing" ? "bg-green-500" : "bg-amber-400"}`} title={s.source === "existing" ? "existing website" : "new / unverified"} />
              <span className="text-text-primary max-w-[200px] truncate" title={s.url}>{s.name || s.url}</span>
              <button type="button" onClick={() => remove(i)} className="text-text-tertiary hover:text-red-600 px-1">×</button>
            </span>
          ))}
          <button type="button" onClick={() => onChange([])} className="text-[11px] text-text-tertiary hover:text-red-600 px-2">clear all</button>
        </div>
      )}
      <p className="text-[11px] text-text-tertiary mt-1">
        <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-500 align-middle" /> existing ·
        <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-400 align-middle ml-1" /> new (created on launch). New ones are provisioned on staging + prod and linked in the console DB.
      </p>
    </div>
  );
}
